import { HttpService } from "@core/services/http.service.ts";
import {
  ComplaintById,
  ComplaintByIdDTO,
  ComplaintGetDTO,
  ComplaintListDTO,
  ComplaintPostDTO,
  complaintPutDTO,
  NatureComplaintDTO,
  SupportTypeModel,
  ComplaintReceivedPdf,
  GuichetUniqueDto,
  MembersGuichetUniqueDto,
} from "./plaintes.model.ts";
import { apiURL } from "@utils/environment.ts";
import { PaginateResponse } from "@utils/appUtils.ts";
import { jsPDF } from "jspdf";
import { getImageBase64 } from "@utils/imageUtils.ts";
import { ApiMethod } from "@core/interfaces/api.ts";

export class ComplaintService extends HttpService {
  private APIURL = apiURL;

  getAllNatureComplaint() {
    this.urlAPI = this.APIURL + "/nature";
    // return listsNature
    return this.doGet<NatureComplaintDTO[]>();
  }

  getSupportType() {
    this.urlAPI = this.APIURL + "/supportType";
    return this.doGet<SupportTypeModel[]>();
  }

  getComplaintDetails(id: number) {
    this.urlAPI = this.APIURL + "/complaint/" + id;
    return this.doGet<ComplaintById>();
  }

  archiveComplaint(id: number, archived: {}) {
    this.urlAPI = this.APIURL + "/complaint/archived/" + id;
    return this.doPut<any, {}>(archived);
  }

  getAllComplaintWithPagination(params: {
    page: number;
    size: number;
    sortBy?: string;
    sortDir?: string;
    search?: string;
    status?: string;
    type?: number | string;
    startDate?: string;
    endDate?: string;
    receivedComplaint?: boolean;
  }): Promise<PaginateResponse<ComplaintListDTO>> {
    const queryParams = new URLSearchParams({
      page: params.page.toString(),
      size: params.size.toString(),
      sortBy: params.sortBy ?? "",
      sortDir: params.sortDir ?? "",
      search: params.search ?? "",
      status: params.status ?? "",
      type: params.type?.toString() ?? "",
      startDate: params.startDate ?? "",
      endDate: params.endDate ?? "",
      receivedComplaint: params.receivedComplaint?.toString() ?? "",
    });
    this.urlAPI = this.APIURL + `/complaint?${queryParams}`;
    return this.doGet<PaginateResponse<ComplaintListDTO>>();
  }

  saveComplaint(complaint: ComplaintPostDTO) {
    this.urlAPI = this.APIURL + "/complaint";
    return this.doPost<any, ComplaintPostDTO>(complaint);
  }

  getComplaint(
    page: number,
    size: number,
    sortBy?: string,
    sortDir?: string,
    search?: string
  ) {
    const params = new URLSearchParams({
      page: page.toString(),
      size: size.toString(),
      sortBy: sortBy?.toString() ?? "",
      sortDir: sortDir?.toString() ?? "",
      search: search?.toString() ?? "",
    });
    this.urlAPI = this.APIURL + `complaint?${params}`;
    return this.doGet<PaginateResponse<ComplaintGetDTO>>();
  }

  getComplaintById(id: number): Promise<ComplaintByIdDTO> {
    this.urlAPI = `${this.APIURL}/complaint/${id}`;
    return this.doGet<ComplaintByIdDTO>();
  }

  editComplaint(id: string, complaint: complaintPutDTO) {
    this.urlAPI = `${this.APIURL}/complaint/${id}`;
    return this.doPut<any, complaintPutDTO>(complaint);
  }

  downloadComplaintReceipt(id: number): void {
    this.urlAPI = `${this.APIURL}/complaint/${id}/receipt`;

    Promise.all([
      this.doGet<ComplaintReceivedPdf>(),
      getImageBase64("/assets/LOGO_MPS.png"),
    ])

      .then(([response, logoBase64]) => {
        const doc = new jsPDF();

        const date = new Date();
        const formattedDate = date.toLocaleDateString("fr-FR");
        const receivedDate = new Date(response.receivedDate);
        const newDateReceived = receivedDate.toLocaleDateString("fr-FR");
        const maxWidth = 170;
        const lineHeight = 8;
        let x = 20;
        let y = 80;
        const segment = [
          { text: "Rosia nandraisana ny fitarainan'i ", bold: false },
          { text: response.complainant, bold: true },
          { text: " mitondra ny nomerao ", bold: false },
          { text: response.ref, bold: true },
          { text: " anaty REGISTRE - natao ny ", bold: false },
          { text: newDateReceived, bold: true },
          { text: " - CPS / IS tao amin'ny fokontany ", bold: false },
          { text: response.fokontany, bold: true },
          { text: ". Ny mpandray an-tanana ny fitarainana : ", bold: false },
          { text: response.agent, bold: true },
          { text: " - Laharan-tarobia afaka hifandraisana : ", bold: false },
          {
            text:
              response.phoneNumber.trim() === ""
                ? "tsy voafaritra"
                : response.phoneNumber,
            bold: true,
          },
        ];
        doc.setFontSize(12)
        const pageWidth = doc.internal.pageSize.getWidth();
        segment.forEach((segment) => {
          doc.setFont("Helvetica", segment.bold ? "bold": "normal")
          const words = segment.text.split(" ")
          words.forEach((word, index) => {
            const spacing = index < words.length - 1 ? " " : ""
            const wordWithSpace = word + spacing
            const wordWidth = doc.getTextWidth(wordWithSpace)
            if(x + wordWidth > 20 + maxWidth){
              y += lineHeight
              x = 20
            }
            doc.text(wordWithSpace, x, y)
            x += wordWidth;
          })
        }) 
        doc.addImage(logoBase64, "PNG", 140, 15, 60, 40);

        const rightSignature = "Ny komity mpiahy ny mpiara-belona - CMS/IS";
        const Ysignature = 150;
        const leftSignature = "Ny mpitaraina";

        doc.setFont("Helvetica", "bold");
        doc.setFontSize(14);
        doc.text("Ministère de la Population et des Solidarités", 20, 30);

        doc.setFontSize(12);
        doc.setFont("Helvetica", "normal");
        doc.text(`Anio, ${formattedDate} `, 20, 40);
        doc.setFont("Helvetica", "bold");
        doc.setFontSize(12);
        doc.text("Rosia fitarainana", 20, 50);

        doc.setFont("Helvetica", "normal");
        doc.setFontSize(12);
        doc.text(leftSignature, 20, Ysignature);
        const rightTextWidth = doc.getTextWidth(rightSignature);
        const xRight = pageWidth - 20 - rightTextWidth;
        doc.text(rightSignature, xRight, Ysignature);
        doc.save(`Reçu_plainte.pdf`);
      })
      .catch((error) => {
        console.error(`Erreur lors du téléchargement du reçu:`, error);
      });
  }

  getComplaintExport(params: {
    page: number;
    size: number;
    sortBy?: string;
    sortDir?: string;
    search?: string;
    status?: string;
    type?: number | string;
    startDate?: string;
    endDate?: string;
    receivedComplaint?: boolean;
  }) {
    const queryParams = new URLSearchParams({
      page: params.page?.toString() ?? "1",
      size: params.size?.toString() ?? "-1",
      sortBy: params.sortBy ?? "receivedDate",
      sortDir: params.sortDir ?? "desc",
      search: params.search ?? "",
      status: params.status ?? "",
      type: params.type!.toString(),
      startDate: params.startDate ?? "",
      endDate: params.endDate ?? "",
      receivedComplaint: (params.receivedComplaint ?? false).toString(),
    });
    this.urlAPI = this.APIURL + `/complaint/export?${queryParams}`;
    return this.doRequest<Blob, undefined>(
      this.urlAPI,
      ApiMethod.GET,
      undefined,
      { responseType: "blob" }
    );
  }

  getGuichetUnique(
    householdId?: string, 
    individualId?: string
  ) {
    this.urlAPI = this.APIURL +`/guichet-unique/complainant?householdId=${householdId}&individualId=${individualId}`;
    return this.doGet<GuichetUniqueDto>();
  }

  getMembersByHouseholdId(householdId: string) {
    this.urlAPI = this.APIURL + `/guichet-unique/household/${householdId}/members`;
    return this.doGet<MembersGuichetUniqueDto[]>();
  }


}
